import * as React from "react";
import { useState, useEffect } from "react";
import {
  Stack,
  Text,
  TextField,
  Dropdown,
  DropdownMenuItemType,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  ActionButton,
  IconButton,
  Spinner,
  SpinnerSize,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import { apiBaseUrl } from "../../../auth/authConfig";
import { authHeaders } from "../../../auth/authDialog";
import {
  BankInfo,
  ScreenKind,
  ScreenCriterionRow,
  ScreenOutputColumn,
  ScreenerState,
} from "../types";
import { ResultRow, resultListStyle } from "./CompanySearch";
import { insertMetricsSheet, MetricColumn } from "../../sheetBuilder";

const API_URL = apiBaseUrl();
const theme = getTheme();

// Pane shows a short list; the full fetch (up to SCREEN_FETCH_LIMIT, ranked by
// assets) is kept in state for the sheet insert.
const RESULT_DISPLAY_LIMIT = 25;
const SCREEN_FETCH_LIMIT = 500;
// Server-side METRICS matrix cap — certs × metric columns must stay under it.
const MATRIX_MAX_CELLS = 10000;

const QUARTER_RE = /^\d{4}Q[1-4]$/;

// Institution fields offered as criteria. Kinds decide the control: text
// (contains/equals), multi (choices fetched from /banks/screen/options), or
// numeric (comparison + value). Mirrors the server's SCREENABLE_INFO_COLUMNS.
const INFO_SCREEN_FIELDS: { key: string; label: string; kind: ScreenKind }[] = [
  { key: "name", label: "Name", kind: "text" },
  { key: "city", label: "City", kind: "text" },
  { key: "state", label: "State", kind: "multi" },
  { key: "county", label: "County", kind: "text" },
  { key: "zip", label: "ZIP", kind: "text" },
  { key: "bkclass", label: "Bank class", kind: "multi" },
  { key: "chrtagnt", label: "Chartering agency", kind: "multi" },
  { key: "specgrpn", label: "Specialization", kind: "multi" },
  { key: "offices", label: "Offices", kind: "numeric" },
  { key: "asset", label: "Total assets ($000s)", kind: "numeric" },
  { key: "dep", label: "Total deposits ($000s)", kind: "numeric" },
];

const TEXT_OPS: IDropdownOption[] = [
  { key: "contains", text: "contains" },
  { key: "eq", text: "equals" },
];

const NUMERIC_OPS: IDropdownOption[] = [
  { key: "lt", text: "<" },
  { key: "lte", text: "≤" },
  { key: "eq", text: "=" },
  { key: "gte", text: "≥" },
  { key: "gt", text: ">" },
];

type MetricFieldDef = { field: string; title: string | null };

type FieldMeta = { type: "info" | "metric"; field: string; label: string; kind: ScreenKind };

const criterionCardStyle = mergeStyles({
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: "4px",
  padding: "8px",
  backgroundColor: theme.palette.neutralLighterAlt,
});

const chipStyle = mergeStyles({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  fontSize: 11,
  padding: "2px 4px 2px 8px",
  borderRadius: 10,
  border: `1px solid ${theme.palette.neutralLight}`,
  backgroundColor: theme.palette.neutralLighter,
});

// "info:state" → { type, field } plus display metadata.
function parseFieldKey(fieldKey: string, metricFields: MetricFieldDef[]): FieldMeta {
  const sep = fieldKey.indexOf(":");
  const type = fieldKey.slice(0, sep) as "info" | "metric";
  const field = fieldKey.slice(sep + 1);
  if (type === "metric") {
    const def = metricFields.filter((m) => m.field === field)[0];
    return { type, field, label: def?.title ? `${field} — ${def.title}` : field, kind: "numeric" };
  }
  const info = INFO_SCREEN_FIELDS.filter((f) => f.key === field)[0];
  return { type, field, label: info?.label ?? field, kind: info?.kind ?? "text" };
}

export interface CompanyScreenerProps {
  screener: ScreenerState;
  onScreenerChange: (state: ScreenerState) => void;
  selectedCert: number | null;
  onSelect: (bank: BankInfo) => void;
}

const CompanyScreener: React.FunctionComponent<CompanyScreenerProps> = ({
  screener,
  onScreenerChange,
  selectedCert,
  onSelect,
}) => {
  const [metricFields, setMetricFields] = useState<MetricFieldDef[]>([]);
  const [multiOptions, setMultiOptions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insertNote, setInsertNote] = useState<string | null>(null);
  const [inserting, setInserting] = useState(false);

  // Output-column picker (local — only committed rows live in screener state).
  const [outputFieldKey, setOutputFieldKey] = useState<string>("metric:ROE");
  const [outputQuarter, setOutputQuarter] = useState<string>("");
  const [showColumns, setShowColumns] = useState(false);

  // Screenable metric fields from the glossary (usage metrics/both).
  useEffect(() => {
    fetch(`${API_URL}/fields`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((all: { field: string; title: string | null; usage: string | null }[]) =>
        setMetricFields(all.filter((f) => f.usage === "metrics" || f.usage === "both"))
      )
      .catch(() => setError("Could not load metric field list."));
  }, []);

  // Choices for multi-select fields (state, bkclass, …).
  useEffect(() => {
    fetch(`${API_URL}/banks/screen/options`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then(setMultiOptions)
      .catch(() => {
        // Non-fatal: multi-select dropdowns stay empty until signed in.
      });
  }, []);

  const fieldOptions: IDropdownOption[] = [
    { key: "hdr-info", text: "Institution", itemType: DropdownMenuItemType.Header },
    ...INFO_SCREEN_FIELDS.map((f) => ({ key: `info:${f.key}`, text: f.label })),
    { key: "hdr-metric", text: "Financial", itemType: DropdownMenuItemType.Header },
    ...metricFields.map((m) => ({
      key: `metric:${m.field}`,
      text: m.title ? `${m.field} — ${m.title}` : m.field,
    })),
  ];

  const update = (patch: Partial<ScreenerState>) => onScreenerChange({ ...screener, ...patch });

  const updateCriterion = (id: number, patch: Partial<ScreenCriterionRow>) =>
    update({ criteria: screener.criteria.map((c) => (c.id === id ? { ...c, ...patch } : c)) });

  const addCriterion = () => {
    const nextId = screener.criteria.reduce((max, c) => Math.max(max, c.id), 0) + 1;
    update({
      criteria: [
        ...screener.criteria,
        { id: nextId, fieldKey: "info:state", op: "eq", value: "", values: [], quarter: "" },
      ],
    });
  };

  const removeCriterion = (id: number) =>
    update({ criteria: screener.criteria.filter((c) => c.id !== id) });

  // A criterion is complete when its control has a usable value (and, for
  // metrics, the quarter is blank = latest or a valid YYYYQN).
  const criterionComplete = (c: ScreenCriterionRow): boolean => {
    const meta = parseFieldKey(c.fieldKey, metricFields);
    const quarter = c.quarter.trim().toUpperCase();
    if (meta.type === "metric" && quarter && !QUARTER_RE.test(quarter)) return false;
    if (meta.kind === "multi") return c.values.length > 0;
    if (meta.kind === "numeric") return c.value.trim() !== "" && !isNaN(Number(c.value));
    return c.value.trim() !== "";
  };

  const allComplete = screener.criteria.length > 0 && screener.criteria.every(criterionComplete);

  const runScreen = async () => {
    setLoading(true);
    setError(null);
    setInsertNote(null);
    try {
      const criteria = screener.criteria.map((c) => {
        const meta = parseFieldKey(c.fieldKey, metricFields);
        if (meta.kind === "multi") {
          return { type: meta.type, field: meta.field, op: "in", values: c.values };
        }
        if (meta.kind === "numeric") {
          const quarter = meta.type === "metric" ? c.quarter.trim().toUpperCase() : "";
          return quarter
            ? { type: meta.type, field: meta.field, op: c.op, value: Number(c.value), quarter }
            : { type: meta.type, field: meta.field, op: c.op, value: Number(c.value) };
        }
        return { type: meta.type, field: meta.field, op: c.op, value: c.value.trim() };
      });

      const response = await fetch(`${API_URL}/banks/screen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ logic: screener.logic, limit: SCREEN_FETCH_LIMIT, criteria }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? `Request failed: ${response.status}`);
      }
      const data = await response.json();
      update({ results: data.results, total: data.total });
    } catch (err) {
      setError(`Screen failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  // --- Output columns (extra sheet columns) ---

  const addOutput = () => {
    const meta = parseFieldKey(outputFieldKey, metricFields);
    const quarter = meta.type === "metric" ? outputQuarter.trim().toUpperCase() : "";
    if (quarter && !QUARTER_RE.test(quarter)) {
      setError(`Invalid quarter '${quarter}' — use e.g. 2025Q4, or leave blank for latest.`);
      return;
    }
    setError(null);
    const exists = screener.outputs.filter(
      (o) => o.fieldKey === outputFieldKey && o.quarter === quarter
    ).length > 0;
    if (!exists) {
      update({ outputs: [...screener.outputs, { fieldKey: outputFieldKey, quarter }] });
    }
    setOutputQuarter("");
  };

  const removeOutput = (index: number) =>
    update({ outputs: screener.outputs.filter((_o, i) => i !== index) });

  // --- Insert results sheet ---

  // Sheet columns: Cert + Name, the fields the screen used, then the extra
  // output columns. Metric criteria columns show the value the screen matched
  // against (the screened quarter, or latest); dedupe on field+quarter.
  const insertSheet = async () => {
    if (!screener.results || screener.results.length === 0) return;
    setInserting(true);
    setInsertNote(null);
    setError(null);
    try {
      const infoColumns: string[] = ["Name"];
      const metricColumns: MetricColumn[] = [];
      const seenInfo: Record<string, boolean> = { name: true };
      const seenMetric: Record<string, boolean> = {};

      const pushInfo = (field: string) => {
        if (!seenInfo[field]) {
          seenInfo[field] = true;
          // Header doubles as the live INFO field input; capitalize for looks.
          infoColumns.push(field.charAt(0).toUpperCase() + field.slice(1));
        }
      };
      const pushMetric = (field: string, quarter: string) => {
        const key = `${field}|${quarter}`;
        if (!seenMetric[key]) {
          seenMetric[key] = true;
          metricColumns.push(quarter ? { field, quarter } : { field });
        }
      };

      for (const c of screener.criteria) {
        const meta = parseFieldKey(c.fieldKey, metricFields);
        if (meta.type === "info") pushInfo(meta.field);
        else pushMetric(meta.field, c.quarter.trim().toUpperCase());
      }
      for (const o of screener.outputs) {
        const meta = parseFieldKey(o.fieldKey, metricFields);
        if (meta.type === "info") pushInfo(meta.field);
        else pushMetric(meta.field, o.quarter);
      }

      // Respect the server's matrix cap: trim certs, keeping the top-by-assets.
      let certs = screener.results.map((b) => b.cert);
      const maxCerts =
        metricColumns.length > 0 ? Math.floor(MATRIX_MAX_CELLS / metricColumns.length) : certs.length;
      if (certs.length > maxCerts) certs = certs.slice(0, maxCerts);

      const sheetName = await insertMetricsSheet({
        baseName: "SheetyBanks Screen",
        certs,
        infoColumns,
        metricColumns,
      });

      const note =
        certs.length < screener.total
          ? `Inserted "${sheetName}" with the top ${certs.length} of ${screener.total} matches (by assets).`
          : `Inserted "${sheetName}" with all ${certs.length} matches.`;
      setInsertNote(note);
    } catch (err) {
      setError(`Insert failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInserting(false);
    }
  };

  // --- Render ---

  const renderCriterion = (c: ScreenCriterionRow) => {
    const meta = parseFieldKey(c.fieldKey, metricFields);
    return (
      <div key={c.id} className={criterionCardStyle}>
        <Stack tokens={{ childrenGap: 6 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
              <Dropdown
                options={fieldOptions}
                selectedKey={c.fieldKey}
                onChange={(_e, opt) =>
                  opt &&
                  updateCriterion(c.id, {
                    fieldKey: String(opt.key),
                    // Reset the control state when the field kind changes.
                    op: parseFieldKey(String(opt.key), metricFields).kind === "text" ? "contains" : "gte",
                    value: "",
                    values: [],
                    quarter: "",
                  })
                }
                styles={{ dropdown: { minWidth: 0 } }}
              />
            </Stack.Item>
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              ariaLabel="Remove criterion"
              onClick={() => removeCriterion(c.id)}
              styles={{ root: { height: 28, width: 28 } }}
            />
          </Stack>

          {meta.kind === "multi" && (
            <Dropdown
              multiSelect
              placeholder={multiOptions[meta.field] ? "Select values…" : "Sign in to load choices…"}
              options={(multiOptions[meta.field] ?? []).map((v) => ({ key: v, text: v }))}
              selectedKeys={c.values}
              onChange={(_e, opt) => {
                if (!opt) return;
                const next = opt.selected
                  ? [...c.values, String(opt.key)]
                  : c.values.filter((v) => v !== String(opt.key));
                updateCriterion(c.id, { values: next });
              }}
            />
          )}

          {meta.kind !== "multi" && (
            <Stack horizontal tokens={{ childrenGap: 4 }}>
              <Dropdown
                options={meta.kind === "text" ? TEXT_OPS : NUMERIC_OPS}
                selectedKey={c.op}
                onChange={(_e, opt) => opt && updateCriterion(c.id, { op: String(opt.key) })}
                styles={{ root: { width: meta.kind === "text" ? 110 : 64 } }}
              />
              <Stack.Item grow>
                <TextField
                  placeholder={meta.kind === "numeric" ? "value" : "text"}
                  value={c.value}
                  onChange={(_e, val) => updateCriterion(c.id, { value: val || "" })}
                  onKeyDown={(e) => e.key === "Enter" && allComplete && runScreen()}
                />
              </Stack.Item>
              {meta.type === "metric" && (
                <TextField
                  placeholder="latest"
                  title="Quarter to screen against, e.g. 2021Q4 — blank means latest"
                  value={c.quarter}
                  onChange={(_e, val) => updateCriterion(c.id, { quarter: val || "" })}
                  onKeyDown={(e) => e.key === "Enter" && allComplete && runScreen()}
                  errorMessage={
                    c.quarter.trim() && !QUARTER_RE.test(c.quarter.trim().toUpperCase())
                      ? "e.g. 2021Q4"
                      : undefined
                  }
                  styles={{ root: { width: 96 } }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </div>
    );
  };

  const outputChips = screener.outputs.map((o: ScreenOutputColumn, i: number) => {
    const meta = parseFieldKey(o.fieldKey, metricFields);
    const label = meta.type === "metric" ? `${meta.field} (${o.quarter || "latest"})` : meta.label;
    return (
      <span key={`${o.fieldKey}|${o.quarter}`} className={chipStyle}>
        {label}
        <IconButton
          iconProps={{ iconName: "Cancel" }}
          ariaLabel={`Remove ${label}`}
          onClick={() => removeOutput(i)}
          styles={{ root: { height: 18, width: 18 }, icon: { fontSize: 9 } }}
        />
      </span>
    );
  });

  const shown = screener.results ? screener.results.slice(0, RESULT_DISPLAY_LIMIT) : [];
  const outputMeta = parseFieldKey(outputFieldKey, metricFields);

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Dropdown
        label="Match"
        options={[
          { key: "and", text: "All criteria (AND)" },
          { key: "or", text: "Any criterion (OR)" },
        ]}
        selectedKey={screener.logic}
        onChange={(_e, opt) => opt && update({ logic: opt.key as "and" | "or" })}
        styles={{ root: { maxWidth: 220 } }}
      />

      <Stack tokens={{ childrenGap: 8 }}>{screener.criteria.map(renderCriterion)}</Stack>

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <ActionButton iconProps={{ iconName: "Add" }} onClick={addCriterion} styles={{ root: { height: 28 } }}>
          Add criterion
        </ActionButton>
        <PrimaryButton
          text="Run screen"
          onClick={runScreen}
          disabled={loading || !allComplete}
          styles={{ root: { height: 28 } }}
        />
      </Stack>

      {loading && <Spinner size={SpinnerSize.medium} label="Screening..." />}
      {error && <Text styles={{ root: { color: theme.palette.redDark } }}>{error}</Text>}

      {!loading && screener.results && (
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
            {screener.total === 0
              ? "No banks match this screen."
              : screener.total > shown.length
                ? `${screener.total} banks match — showing top ${shown.length} by assets`
                : `${screener.total} bank${screener.total === 1 ? "" : "s"} match`}
          </Text>

          {/* Export actions live ABOVE the (long, scrolling) results list so
              they're visible the moment a screen returns. */}
          {screener.results.length > 0 && (
            <>
              <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                <PrimaryButton
                  text={inserting ? "Inserting…" : "Insert results sheet"}
                  iconProps={{ iconName: "PageAdd" }}
                  onClick={insertSheet}
                  disabled={inserting}
                  styles={{ root: { height: 28 } }}
                />
                <ActionButton
                  iconProps={{ iconName: showColumns ? "ChevronUp" : "ChevronDown" }}
                  onClick={() => setShowColumns(!showColumns)}
                  styles={{ root: { height: 28 } }}
                >
                  {`Sheet columns${screener.outputs.length > 0 ? ` (${screener.outputs.length})` : ""}`}
                </ActionButton>
              </Stack>

              {insertNote && (
                <Text variant="small" styles={{ root: { color: theme.palette.greenDark } }}>
                  {insertNote}
                </Text>
              )}

              {showColumns && (
                <Stack tokens={{ childrenGap: 6 }}>
                  <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    The sheet always includes certs, names, and the fields you screened on, as a
                    live METRICS table. Add extra columns here (quarter optional — blank means
                    latest).
                  </Text>
                  <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="end">
                    <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                      <Dropdown
                        options={fieldOptions}
                        selectedKey={outputFieldKey}
                        onChange={(_e, opt) => opt && setOutputFieldKey(String(opt.key))}
                      />
                    </Stack.Item>
                    {outputMeta.type === "metric" && (
                      <TextField
                        placeholder="2025Q4"
                        value={outputQuarter}
                        onChange={(_e, val) => setOutputQuarter(val || "")}
                        styles={{ root: { width: 76 } }}
                      />
                    )}
                    <DefaultButton text="Add" onClick={addOutput} styles={{ root: { minWidth: 0, height: 32 } }} />
                  </Stack>
                  {outputChips.length > 0 && (
                    <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
                      {outputChips}
                    </Stack>
                  )}
                </Stack>
              )}

              <div className={resultListStyle}>
                {shown.map((bank) => (
                  <ResultRow key={bank.cert} bank={bank} selected={bank.cert === selectedCert} onSelect={onSelect} />
                ))}
              </div>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default CompanyScreener;
